import { Box, FormControl, InputAdornment, InputLabel, MenuItem, Select, TextField, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import type { Dayjs } from "dayjs";
import { useState } from "react";
import { filterPanelSx } from "../common/filterStyles";
import { presets, type PresetKey } from "../common/dataFilterPresets";
import { EVIDENCE_ACTIVITY_OUTCOMES, EVIDENCE_ACTIVITY_PROCESSORS, EVIDENCE_ACTIVITY_RESULT_FILTERS } from "../../types/evidenceActivity";
import { OUTCOME_LABELS, PROCESSOR_LABELS, RESULT_LABELS } from "./formatDuration";

export type ActivityPreset = Extract<PresetKey, "7D" | "thisMonth" | "30D" | "lastMonth" | "90D" | "custom">;
const ACTIVITY_PRESETS: ActivityPreset[] = ["7D", "thisMonth", "30D", "lastMonth", "90D", "custom"];

export interface ActivityAgentOption { userId: number; name: string }

interface Props {
  preset: ActivityPreset;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  onPresetChange: (preset: ActivityPreset) => void;
  onStartDateChange: (date: Dayjs | null) => void;
  onEndDateChange: (date: Dayjs | null) => void;
  agents: ActivityAgentOption[];
  userId: number | "";
  onUserIdChange: (userId: number | "") => void;
  processor: string;
  onProcessorChange: (processor: string) => void;
  outcome: string;
  onOutcomeChange: (outcome: string) => void;
  result: string;
  onResultChange: (result: string) => void;
  search: string;
  onSearchChange: (search: string) => void;
}

export default function EvidenceActivityFilters(p: Props) {
  const [startOpen, setStartOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {/* The shared bar hides the calendar icon; this page shows it, since the range is what the page is about. */}
      <Box sx={{ ...filterPanelSx, "& .MuiPickersInputBase-root .MuiInputAdornment-positionEnd": { display: "flex" } }}>
        <Box display="flex" alignItems="center" flexWrap="wrap" gap={2} sx={{ p: 2 }}>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel id="activity-range-label">Range</InputLabel>
            <Select labelId="activity-range-label" id="activity-range" label="Range" value={p.preset} onChange={(e) => p.onPresetChange(e.target.value as ActivityPreset)}>
              {ACTIVITY_PRESETS.map((key) => <MenuItem key={key} value={key}>{presets[key].label}</MenuItem>)}
            </Select>
          </FormControl>
          <Box display="flex" alignItems="center" gap={1}>
            <DatePicker
              label="Start" format="MM/DD/YYYY" value={p.startDate} onChange={p.onStartDateChange}
              open={startOpen} onOpen={() => setStartOpen(true)} onClose={() => setStartOpen(false)}
              slotProps={{ textField: { size: "small", sx: { width: 150 }, onClick: () => setStartOpen(true) } }}
            />
            <Typography variant="body2" color="text.secondary">—</Typography>
            <DatePicker
              label="End" format="MM/DD/YYYY" value={p.endDate} onChange={p.onEndDateChange}
              open={endOpen} onOpen={() => setEndOpen(true)} onClose={() => setEndOpen(false)}
              slotProps={{ textField: { size: "small", sx: { width: 150 }, onClick: () => setEndOpen(true) } }}
            />
          </Box>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="activity-agent-label">Agent</InputLabel>
            <Select
              labelId="activity-agent-label" id="activity-agent" label="Agent" value={p.userId}
              onChange={(e) => p.onUserIdChange(e.target.value as number | "")}
              MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
            >
              <MenuItem value="">All agents</MenuItem>
              {p.agents.map((a) => <MenuItem key={a.userId} value={a.userId}>{a.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel id="activity-processor-label">Processor</InputLabel>
            <Select labelId="activity-processor-label" id="activity-processor" label="Processor" value={p.processor} onChange={(e) => p.onProcessorChange(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {EVIDENCE_ACTIVITY_PROCESSORS.map((key) => <MenuItem key={key} value={key}>{PROCESSOR_LABELS[key]}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel id="activity-outcome-label">Outcome</InputLabel>
            <Select labelId="activity-outcome-label" id="activity-outcome" label="Outcome" value={p.outcome} onChange={(e) => p.onOutcomeChange(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {EVIDENCE_ACTIVITY_OUTCOMES.map((key) => <MenuItem key={key} value={key}>{OUTCOME_LABELS[key]}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel id="activity-result-label">Result</InputLabel>
            <Select labelId="activity-result-label" id="activity-result" label="Result" value={p.result} onChange={(e) => p.onResultChange(e.target.value)}>
              <MenuItem value="">Any</MenuItem>
              {EVIDENCE_ACTIVITY_RESULT_FILTERS.map((key) => <MenuItem key={key} value={key}>{RESULT_LABELS[key]}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            id="activity-search"
            size="small"
            placeholder="Dispute #, order #, agent, company"
            value={p.search}
            onChange={(e) => p.onSearchChange(e.target.value)}
            slotProps={{
              htmlInput: { "aria-label": "Search" },
              input: { startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "text.secondary", fontSize: 18 }} /></InputAdornment> },
            }}
            sx={{ flex: "1 1 220px", minWidth: { sm: 220 } }}
          />
        </Box>
      </Box>
    </LocalizationProvider>
  );
}
